import React, { useMemo, useState } from "react";

const CATS = [
  {
    key: "quality",
    label: "Quality",
    origin: "left",
    icon: (
      <svg width="42" height="42" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 7h16M4 12h10M4 17h16" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      </svg>
    ),
    blurb:
      "HD Labs is built around consistency. Every release is handled with precision — from presentation to labeling — to ensure customers receive clean, clear, and reliable product information every time.",
    bullets: ["Clear labeling", "Batch consistency", "Clean presentation"],
  },
  {
    key: "documentation",
    label: "Documentation",
    origin: "center",
    icon: (
      <svg width="42" height="42" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 4h10v16H7z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
        <path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      </svg>
    ),
    blurb:
      "Each product is presented with structured, reference-ready information. Key details are organized intentionally so customers can verify and compare products quickly, without confusion or clutter.",
    bullets: ["Standardized fields", "Reference-ready layout", "Storage guidance"],
  },
  {
    key: "service",
    label: "Service",
    origin: "right",
    icon: (
      <svg width="42" height="42" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 11V8a5 5 0 0 1 10 0v3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        <path d="M6 11h12v10H6V11Z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
      </svg>
    ),
    blurb:
      "A premium product experience goes beyond the vial. Our site is designed to be smooth, transparent, and support-ready — with clear policies, secure checkout flow, and straightforward communication.",
    bullets: ["Secure checkout flow", "Support-ready structure", "Clear navigation"],
  },
];

function cn(...s) {
  return s.filter(Boolean).join(" ");
}

function originClass(o) {
  if (o === "left") return "origin-left";
  if (o === "right") return "origin-right";
  return "origin-center";
}

export default function Standards() {
  const [hovered, setHovered] = useState(null);
  const active = useMemo(() => CATS.find((x) => x.key === hovered) || null, [hovered]);

  return (
    <section id="standards" className="relative overflow-hidden bg-blue-900 py-24">
      {/* background wash */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/10" />
        <div className="absolute -left-56 -top-56 h-[720px] w-[720px] rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -right-56 top-28 h-[720px] w-[720px] rounded-full bg-cyan-200/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header (more catchy + minimal) */}
        <div className="max-w-3xl text-white">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-white/80">
            <span className="h-1.5 w-1.5 rounded-full bg-white/80" />
            Standards
            <span className="mx-1 h-3 w-px bg-white/20" />
            Research use only
          </div>

          <h2 className="mt-4 text-4xl font-semibold tracking-tight sm:text-5xl">
            Built to be{" "}
            <span className="text-white underline decoration-white/25 underline-offset-8">
              consistent
            </span>
            .
          </h2>

          <p className="mt-3 text-sm sm:text-base leading-relaxed text-white/80 max-w-2xl">
            Quality, documentation, and service — structured to keep every release clean, clear, and repeatable.
          </p>
        </div>

        {/* Stage */}
        <div className="mt-14">
          <div className="relative mx-auto max-w-[980px] h-[210px] sm:h-[230px] isolate">
            {/* Overlay */}
            <div
              className={cn(
                "absolute inset-0 z-30 rounded-[999px] overflow-hidden transition-opacity duration-200",
                hovered
                  ? "opacity-100 pointer-events-auto border border-white/60 shadow-[0_40px_90px_-55px_rgba(0,0,0,0.75)]"
                  : "opacity-0 pointer-events-none border border-transparent shadow-none"
              )}
              onMouseLeave={() => setHovered(null)}
              aria-hidden="true"
            >
              {/* Directional wipe layer */}
              <div
                className={cn(
                  "absolute inset-0 backdrop-blur-2xl bg-blue-900/95",
                  "transform-gpu",
                  hovered ? "scale-x-100" : "scale-x-0",
                  originClass(active?.origin || "center")
                )}
                style={{
                  willChange: "transform",
                  transitionProperty: "transform",
                  transitionDuration: "780ms",
                  transitionTimingFunction: "cubic-bezier(.12,1,.18,1)",
                  transitionDelay: hovered ? "90ms" : "0ms",
                }}
              />

              {/* Content */}
              {active && (
                <div
                  className="relative h-full w-full px-10 sm:px-14 flex items-center select-none"
                  style={{
                    transition: "opacity 340ms ease, transform 340ms ease",
                    transitionDelay: hovered ? "220ms" : "0ms",
                    opacity: hovered ? 1 : 0,
                    transform: hovered ? "translateY(0px)" : "translateY(6px)",
                  }}
                >
                  <div className="flex items-start sm:items-center gap-8">
                    <div className="text-white/95 mt-0.5">{active.icon}</div>

                    <div className="text-white">
                      <div className="text-2xl sm:text-3xl font-semibold tracking-tight">{active.label}</div>

                      <div className="mt-2 text-sm sm:text-base text-white/85 leading-relaxed max-w-2xl">
                        {active.blurb}
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {active.bullets.map((b) => (
                          <span
                            key={b}
                            className="inline-flex items-center rounded-full bg-white/12 px-3 py-1 text-xs font-semibold text-white/90 border border-white/10"
                          >
                            {b}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Circles */}
            <div className="absolute inset-0 z-10 flex items-center justify-between">
              {CATS.map((cat) => {
                const isOther = hovered && hovered !== cat.key;
                const isActive = hovered === cat.key;

                return (
                  <button
                    key={cat.key}
                    type="button"
                    onMouseEnter={() => setHovered(cat.key)}
                    onFocus={() => setHovered(cat.key)}
                    onBlur={() => setHovered(null)}
                    className={cn(
                      "relative transform-gpu will-change-transform",
                      "h-[170px] w-[170px] sm:h-[190px] sm:w-[190px]",
                      "rounded-full bg-white/10 border border-white/70",
                      "shadow-[0_25px_60px_-35px_rgba(0,0,0,0.55)]",
                      "transition-[transform,opacity,filter] duration-[520ms] ease-[cubic-bezier(.16,1,.3,1)]",
                      "focus:outline-none focus:ring-2 focus:ring-white/35",
                      isOther && "scale-[0.92] opacity-70 blur-[0.2px]",
                      isActive && "scale-[1.02]"
                    )}
                    aria-label={cat.label}
                    style={{
                      transitionDelay: isOther ? "60ms" : "0ms",
                    }}
                  >
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-white">
                      <div className={cn("transition-transform duration-250 ease-out", isActive && "scale-[1.12]")}>
                        {cat.icon}
                      </div>

                      <div className="text-sm sm:text-base font-semibold tracking-tight text-white/95">
                        {cat.label}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-10 text-center text-xs text-white/60">
            Research use only. Not intended for human consumption.
          </div>
        </div>
      </div>
    </section>
  );
}